/**
 * File-related utility functions for formatting and calculating sizes.
 */

/**
 * Threshold for switching to TextEncoder (avoids Blob overhead for large strings).
 * Below this, Blob is actually faster due to less function call overhead.
 */
const LARGE_STRING_THRESHOLD = 65536; // 64KB

/**
 * Reusable TextEncoder instance for large string size calculations.
 */
const textEncoder = new TextEncoder();

/**
 * Calculates the size of a string in bytes (UTF-8 encoding).
 *
 * Uses Blob for small strings (faster) and TextEncoder for large strings
 * (lower memory overhead - important for XFDF that can be several MB).
 *
 * @param str - The string to measure
 * @returns The size in bytes
 *
 * @example
 * ```typescript
 * getStringSizeBytes("hello") // 5
 * getStringSizeBytes("héllo") // 6 (é is 2 bytes in UTF-8)
 * ```
 */
export function getStringSizeBytes(str: string): number {
  if (str.length < LARGE_STRING_THRESHOLD) {
    return new Blob([str]).size;
  }
  return textEncoder.encode(str).length;
}

/**
 * Formats a byte count to a human-readable string.
 *
 * @param bytes - The number of bytes
 * @param decimals - Number of decimal places (default: 2)
 * @returns A formatted string (e.g., "1.50 KB", "2.00 MB")
 *
 * @remarks
 * - Uses logarithmic calculation to determine the appropriate unit
 * - Values beyond PB are capped at PB unit to prevent array bounds errors
 * - Rounding via `toFixed` may cause boundary values to display in the next unit
 *   (e.g., 1048575 bytes rounds to "1024.00 KB" instead of "1023.00 KB")
 *
 * @example
 * ```typescript
 * formatBytes(500)        // "500 B"
 * formatBytes(1536)       // "1.50 KB"
 * formatBytes(1536, 1)    // "1.5 KB"
 * formatBytes(1048576)    // "1.00 MB"
 * formatBytes(1073741824) // "1.00 GB"
 * ```
 */
export function formatBytes(bytes: number, decimals = 2): string {
  if (bytes < 1024) return `${bytes} B`;

  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB", "PB"];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), sizes.length - 1);

  return `${(bytes / k ** i).toFixed(decimals)} ${sizes[i]}`;
}
